import { ApPayeeRef } from '@/components/ApPayee';
import {
  useSelectedApPayee,
  useUpdateSelectedApPayee,
} from '@/components/ApPayee/useChangeApPayee';
import { Props as InstantPayeeFormProps } from '@/components/InstantPayeeForm';
import { ApReportFormInputInvoiceInstantPayee } from '@/components/ReportForm/ApReportFormInputInvoiceInstantPayee';
import {
  ApReportFormInputPayeeSelect,
  type Props as ApReportFormInputPayeeSelectProps,
} from '@/components/ReportForm/ApReportFormInputPayeeSelect';
import {
  InputBaseType,
  SelectType,
} from '@/components/ReportForm/InputBase/types';
import {
  useUpdatePayeeRelatedInfo,
  type AppliedPayeeValues,
} from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useChangePayeeValue';
import { useConfirm } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useConfirm';
import { useIsActiveInstantPayee } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useIsActiveInstantPayee';
import { useSyncSelectedApPayeeIsBank } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useSyncSelectedApPayeeIsBank';
import { createApPayeeDetail } from '@/components/ReportForm/InstantPayeeSwitcher/utils';
import { SelectedType } from '@/components/ReportForm/Modals/ChangeXXX/type';
import { useLazyGetApPayeeDetail } from '@/context/services/ap_payee/ApPayee.service';
import { TPMData } from '@/context/services/tpm/type';
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Control,
  FieldPath,
  FieldValues,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = Omit<
  InputBaseType<TFieldValues>,
  'required'
> & {
  control: Control<TFieldValues>;
  className?: string;
  bookDateName: FieldPath<TFieldValues>;
  dueDateName: FieldPath<TFieldValues>;
  invoiceRegistrationNumberName: FieldPath<TFieldValues> | undefined;
  isInstantPayeeEnabled: boolean;
  onCallbackUpdatePayeeRelatedInfo: (
    tpmData: TPMData | undefined
  ) => Promise<void>;
  setDraftInstantPayeeBankName?: Dispatch<SetStateAction<string | undefined>>;
  setDraftInstantPayeeBankBranchName?: Dispatch<
    SetStateAction<string | undefined>
  >;
} & InstantPayeeFormProps<TFieldValues> &
  SelectType & {
    onChangeAfter?: VoidFunction;
    onAfterUpdatePayeeRelatedInfo?: (applied?: AppliedPayeeValues) => void;
    prefixIndicator?: ApReportFormInputPayeeSelectProps<TFieldValues>['prefixIndicator'];
  };

const InnerInstantPayeeSwitcher = <TFieldValues extends FieldValues>({
  control,
  instantPayeeFieldNames,
  description: instantPayeeFieldCaption,
  name,
  label,
  inputId,
  caption,
  bookDateName,
  dueDateName,
  invoiceRegistrationNumberName,
  isInstantPayeeEnabled,
  defaultOptions,
  onCallbackUpdatePayeeRelatedInfo,
  setDraftInstantPayeeBankName,
  setDraftInstantPayeeBankBranchName,
  onChangeAfter,
  onAfterUpdatePayeeRelatedInfo,
  prefixIndicator,
}: Props<TFieldValues>) => {
  const { clearErrors, getValues, setValue, getFieldState, watch } =
    useFormContext<TFieldValues>();
  const formContext = useMemo(
    () => ({ getValues, setValue, watch }),
    [getValues, setValue, watch]
  );
  const { isTouched, isDirty } = getFieldState(name);
  const { errors } = useFormState({ control, name });
  const isActiveInstantPayee = useIsActiveInstantPayee<TFieldValues>({
    control,
    name,
  });
  const InstantPayeeForm = useMemo(() => {
    if (isActiveInstantPayee && isInstantPayeeEnabled) {
      return (
        <ApReportFormInputInvoiceInstantPayee<TFieldValues>
          required
          instantPayeeFieldNames={instantPayeeFieldNames}
          description={instantPayeeFieldCaption}
          control={control}
          setDraftInstantPayeeBankName={setDraftInstantPayeeBankName}
          setDraftInstantPayeeBankBranchName={
            setDraftInstantPayeeBankBranchName
          }
        />
      );
    } else {
      return null;
    }
  }, [
    control,
    instantPayeeFieldCaption,
    instantPayeeFieldNames,
    isActiveInstantPayee,
    isInstantPayeeEnabled,
    setDraftInstantPayeeBankName,
    setDraftInstantPayeeBankBranchName,
  ]);
  useEffect(() => {
    if (!isActiveInstantPayee && Object.keys(errors).length) {
      Object.values(instantPayeeFieldNames).forEach((fieldName) => {
        clearErrors(fieldName);
      });
    }
  }, [errors, clearErrors, instantPayeeFieldNames, isActiveInstantPayee]);
  const inputRef = useRef<ApPayeeRef | null>(null);
  const fetchApPayeeDetail = useLazyGetApPayeeDetail();
  const updateSelectedApPayee = useUpdateSelectedApPayee();
  const syncIsBank = useSyncSelectedApPayeeIsBank();
  const updatePayeeRelatedInfo = useUpdatePayeeRelatedInfo(
    bookDateName,
    dueDateName,
    onCallbackUpdatePayeeRelatedInfo,
    invoiceRegistrationNumberName
  );
  const applyPayeeRelatedInfo = useCallback(
    async (apPayee: Parameters<typeof updatePayeeRelatedInfo>[0]) => {
      let applied: AppliedPayeeValues | undefined;
      try {
        applied = await updatePayeeRelatedInfo(apPayee);
      } finally {
        onAfterUpdatePayeeRelatedInfo?.(applied);
      }
    },
    [updatePayeeRelatedInfo, onAfterUpdatePayeeRelatedInfo]
  );
  const selectedOptionRef = useRef<SelectedType>('all');
  const onChangeSelectedOption = useCallback((value: SelectedType) => {
    selectedOptionRef.current = value;
  }, []);

  const { confirm, ConfirmModal } = useConfirm(onChangeSelectedOption);
  const selectedApPayee = useSelectedApPayee();

  const onUpdateSelectedApPayee = useCallback(
    async (payeeId: string) => {
      const apPayee = await fetchApPayeeDetail(payeeId);
      updateSelectedApPayee(apPayee ?? undefined);
      syncIsBank(apPayee);
      return apPayee;
    },
    [fetchApPayeeDetail, updateSelectedApPayee, syncIsBank]
  );

  const payeeId = useWatch<TFieldValues>({ control, name });
  const onChange = useCallback(
    async (value: string | number | (string | number)[]) => {
      if (!value || value === '1006') {
        const newApPayeeDetail = createApPayeeDetail(
          String(value),
          selectedApPayee
        );
        updateSelectedApPayee(newApPayeeDetail);
        syncIsBank(undefined);
        return;
      }
      if (
        getValues(bookDateName) ||
        getValues(dueDateName) ||
        (invoiceRegistrationNumberName &&
          getValues(invoiceRegistrationNumberName)) ||
        (getValues(bookDateName) &&
          getValues(dueDateName) &&
          value !== payeeId &&
          payeeId !== undefined)
      ) {
        onChangeSelectedOption('all');
        const result = await confirm();
        if (result) {
          const apPayee = await onUpdateSelectedApPayee(String(value));
          if (selectedOptionRef.current === 'all') {
            await applyPayeeRelatedInfo(apPayee);
          }
        } else {
          inputRef.current?.undoSelection();
        }
      } else {
        const apPayee = await onUpdateSelectedApPayee(String(value));
        await applyPayeeRelatedInfo(apPayee);
      }
    },
    [
      getValues,
      bookDateName,
      dueDateName,
      invoiceRegistrationNumberName,
      payeeId,
      selectedApPayee,
      updateSelectedApPayee,
      syncIsBank,
      onChangeSelectedOption,
      confirm,
      onUpdateSelectedApPayee,
      applyPayeeRelatedInfo,
    ]
  );

  useEffect(() => {
    if (payeeId !== undefined && !isTouched && !isDirty) {
      onUpdateSelectedApPayee(payeeId);
    }
  }, [isDirty, isTouched, onUpdateSelectedApPayee, payeeId]);
  return (
    <>
      <ApReportFormInputPayeeSelect<TFieldValues>
        control={control}
        name={name}
        inputId={inputId}
        label={label}
        caption={caption}
        required
        inputRef={inputRef}
        instantPayeeActivated={isInstantPayeeEnabled}
        formContext={formContext}
        defaultOptions={defaultOptions}
        onChange={onChange}
        onChangeAfter={onChangeAfter}
        prefixIndicator={prefixIndicator}
      />
      {InstantPayeeForm}
      <ConfirmModal />
    </>
  );
};

InnerInstantPayeeSwitcher.displayName = 'InnerInstantPayeeSwitcher';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const InstantPayeeSwitcher = memo((props) => (
  <InnerInstantPayeeSwitcher {...props} />
)) as typeof InnerInstantPayeeSwitcher;
InstantPayeeSwitcher.displayName = 'InstantPayeeSwitcher';
